// Type-only so this module stays loadable outside Angular: the custom inbox
// items report's export layer imports it (see reporting-core/README.md).
import type { ActivatedRoute } from '@angular/router';

import {
  getDaysAndMonthsString,
  loadBoolFilterFromGetParams,
  loadIntFilterFromGetParams,
  loadStringFilterFromGetParams,
  propExists
} from '../reporting-core/utils';
import { CapitanLocation } from './location';
import { EntryPass, getCustomerEntryPassStatusDisplay } from './entry-pass';
import { CustomerEntryPassStatus } from './entry-pass-enums';
import { Membership } from './membership';

export enum CustomInboxStatus {
  draft = 'DRA',
  active = 'ACT',
  retired = 'RET'
}

export enum CustomInboxConditionType {
  accountAge = 'accountAge',
  checkInCount = 'checkInCount',
  customerAge = 'customerAge',
  hasAssignedEntryPass = 'hasAssignedEntryPass',
  activeMembershipStatus = 'activeMembershipStatus',
  hasNotBeenInInboxPreviously = 'hasNotBeenInInboxPreviously',
}

export interface CustomInboxConditionParameters {
  location_id?: number | null;
  min_age?: number | null;
  min_age_days?: number | null;
  min_age_months?: number | null;
  min_check_in_count?: number | null;
  max_check_in_count?: number | null;
  max_age?: number | null;
  max_age_days?: number | null;
  max_age_months?: number | null;
  time_period_days?: number | null;
  time_period_months?: number | null;
  min_number_of_uses_remaining?: number | null;
  max_number_of_uses_remaining?: number | null;
  entry_pass_id?: number | null;
  entry_pass_status?: CustomerEntryPassStatus | null;
  has_active_membership?: boolean | null;
  membership_id?: number | null;
}

export interface CustomInboxCondition {
  condition_type: CustomInboxConditionType,
  parameters: CustomInboxConditionParameters
}

export interface CustomInbox {
  id: number;
  url: string;
  name: string;
  status: CustomInboxStatus;
  conditions: CustomInboxCondition[];
  include_customer_profile_alert: boolean;
  customer_profile_alert_color_hex: string;
  prevent_customer_check_in: boolean;
  auto_resolve_time_hours: number | null;
  staff_instruction_text: string;
  num_active_items: number;
  num_all_time_items: number;
  created_at: string;
  updated_at: string;
}

export interface CustomInboxItem {
  id: number;
  url: string;
  inbox_id: number;
  inbox_name: string;
  inbox_staff_instruction_text: string;
  inbox_include_customer_profile_alert: boolean;
  inbox_customer_profile_alert_color_hex: string;
  customer_id: number;
  customer_first_name: string;
  customer_last_name: string;
  customer_birthday: string;
  customer_email: string;
  conditions_met: string;
  auto_resolve_timestamp: string | null;
  is_resolved: boolean;
  resolved_at: string | null;
  resolved_by_id: number | null;
  resolved_by_first_name: string | null;
  resolved_by_last_name: string | null;
  resolved_by_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomInboxFilters {
  search?: string;
  status?: CustomInboxStatus;
}

export interface CustomInboxItemFilters {
  inbox_id?: number;
  customer_id?: number;
  is_resolved?: boolean;
  created_at_min?: string;
  created_at_max?: string;
  created_at_range?: string;
}

export function loadCustomInboxFiltersFromGetParams(activatedRoute: ActivatedRoute): CustomInboxFilters {
  const filters: CustomInboxFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'search', initialQPs);
  loadStringFilterFromGetParams(filters, 'status', initialQPs);

  return filters;
}

export function loadCustomInboxItemFiltersFromGetParams(activatedRoute: ActivatedRoute): CustomInboxItemFilters {
  const filters: CustomInboxItemFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadIntFilterFromGetParams(filters, 'inbox_id', initialQPs);
  loadIntFilterFromGetParams(filters, 'customer_id', initialQPs);
  loadBoolFilterFromGetParams(filters, 'is_resolved', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_range', initialQPs);

  return filters;
}

export function getCustomInboxStatusDisplay(status: CustomInboxStatus): string {
  switch (status) {
    case CustomInboxStatus.draft:
      return 'Draft';
    case CustomInboxStatus.active:
      return 'Active';
    case CustomInboxStatus.retired:
      return 'Retired';
    default:
      return 'Unknown';
  }
}

export function getCustomInboxConditionTypeDescription(conditionType: CustomInboxConditionType | string): string {
  switch (conditionType) {
    case CustomInboxConditionType.accountAge:
      return 'Account age';
    case CustomInboxConditionType.checkInCount:
      return 'Check-in count';
    case CustomInboxConditionType.customerAge:
      return 'Customer age';
    case CustomInboxConditionType.hasAssignedEntryPass:
      return 'Customer has assigned entry pass';
    case CustomInboxConditionType.activeMembershipStatus:
      return 'Active membership status';
    case CustomInboxConditionType.hasNotBeenInInboxPreviously:
      return 'Customer has not been in this inbox previously';
    default:
      return 'Unknown condition type';
  }
}

export function getCustomInboxConditionDescription(condition: CustomInboxCondition,
                                                   locations: CapitanLocation[],
                                                   entryPasses: EntryPass[],
                                                   memberships: Membership[]): string {
  switch (condition.condition_type) {
    case CustomInboxConditionType.accountAge:
      return getAccountAgeDescription(condition.parameters);
    case CustomInboxConditionType.checkInCount:
      return getCheckInCountDescription(condition.parameters, locations);
    case CustomInboxConditionType.customerAge:
      return getCustomerAgeDescription(condition.parameters);
    case CustomInboxConditionType.hasAssignedEntryPass:
      return getAssignedEntryPassDescription(condition.parameters, entryPasses);
    case CustomInboxConditionType.activeMembershipStatus:
      return getActiveMembershipStatusDescription(condition.parameters, memberships);
    case CustomInboxConditionType.hasNotBeenInInboxPreviously:
      return 'Customer has not been in this inbox previously';
    default:
      return 'Unknown condition';
  }
}

function getAccountAgeDescription(parameters: CustomInboxConditionParameters): string {
  const ageLimits: string[] = [];

  if (propExists(parameters.min_age_days) || propExists(parameters.min_age_months)) {
    ageLimits.push(
      'at least ' +
      getDaysAndMonthsString(
        parameters.min_age_days,
        parameters.min_age_months
      )
    );
  }
  if (propExists(parameters.max_age_days) || propExists(parameters.max_age_months)) {
    ageLimits.push(
      'no more than ' +
      getDaysAndMonthsString(
        parameters.max_age_days,
        parameters.max_age_months
      )
    );
  }

  return 'Customer account is ' + ageLimits.join(' and ') + ' old';
}

function getCheckInCountDescription(parameters: CustomInboxConditionParameters,
                                    locations: CapitanLocation[]): string {
  const countLimits: string[] = [];

  const hasExactCount = (
    propExists(parameters.min_check_in_count) &&
    (parameters.min_check_in_count === parameters.max_check_in_count)
  );
  if (hasExactCount) {
    countLimits.push('exactly ' + parameters.min_check_in_count.toString());
  } else {
    if (propExists(parameters.min_check_in_count)) {
      countLimits.push('at least ' + parameters.min_check_in_count.toString());
    }
    if (propExists(parameters.max_check_in_count)) {
      countLimits.push('no more than ' + parameters.max_check_in_count.toString());
    }
  }

  let conditionString = 'Check-in count ';
  if (propExists(parameters.time_period_days) || propExists(parameters.time_period_months)) {
    conditionString += (
      'in last ' +
      getDaysAndMonthsString(
        parameters.time_period_days,
        parameters.time_period_months
      ) +
      ' '
    );
  }
  if (propExists(parameters.location_id)) {
    let locationName = '<unknown location>';
    for (const location of locations) {
      if (location.id === parameters.location_id) {
        locationName = location.name;
      }
    }
    conditionString += 'at ' + locationName + ' ';
  }
  conditionString += 'is ' + countLimits.join(' and ');

  return conditionString;
}

function getCustomerAgeDescription(parameters: CustomInboxConditionParameters): string {
  const ageLimits: string[] = [];

  if (propExists(parameters.min_age)) {
    ageLimits.push('at least ' + parameters.min_age.toString());
  }
  if (propExists(parameters.max_age)) {
    ageLimits.push('no more than ' + parameters.max_age.toString());
  }

  return 'Customer is ' + ageLimits.join(' and ') + ' years old';
}

function getAssignedEntryPassDescription(parameters: CustomInboxConditionParameters,
                                         entryPasses: EntryPass[]): string {

  let conditionString = 'Customer has an assigned entry pass';

  if (propExists(parameters.entry_pass_id)) {
    let entryPassName = '<unknown entry pass>';
    for (const ep of entryPasses) {
      if (ep.id === parameters.entry_pass_id) {
        entryPassName = ep.name;
      }
    }
    conditionString += ' of type "' + entryPassName + '"';
  }

  if (propExists(parameters.entry_pass_status)) {
    conditionString += ' with status ' + getCustomerEntryPassStatusDisplay(
      parameters.entry_pass_status
    );
  }

  const numberOfUsesRemainingLimits: string[] = [];
  const hasExactUsesRemaining = (
    propExists(parameters.min_number_of_uses_remaining) &&
    (parameters.min_number_of_uses_remaining === parameters.max_number_of_uses_remaining)
  );
  if (hasExactUsesRemaining) {
    numberOfUsesRemainingLimits.push(
      'exactly ' + parameters.min_number_of_uses_remaining.toString()
    );
  } else {
    if (propExists(parameters.min_number_of_uses_remaining)) {
      numberOfUsesRemainingLimits.push(
        'at least ' + parameters.min_number_of_uses_remaining.toString()
      );
    }
    if (propExists(parameters.max_number_of_uses_remaining)) {
      numberOfUsesRemainingLimits.push(
        'no more than ' + parameters.max_number_of_uses_remaining.toString()
      );
    }
  }
  if (numberOfUsesRemainingLimits.length > 0) {
    conditionString += ' with ' + numberOfUsesRemainingLimits.join(' and ') + ' uses remaining';
  }

  return conditionString;
}

function getActiveMembershipStatusDescription(parameters: CustomInboxConditionParameters,
                                              memberships: Membership[]): string {
  let conditionString = 'Customer';

  if (parameters.has_active_membership) {
    conditionString += ' has';
  } else {
    conditionString += ' does not have'
  }

  conditionString += ' an active membership';

  if (parameters.membership_id) {
    let membershipName = '<unknown membership>';
    for (const m of memberships) {
      if (m.id === parameters.membership_id) {
        membershipName = m.name;
      }
    }
    conditionString += ' of type "' + membershipName + '"';
  }

  return conditionString;
}
